// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

/**
 * Detect if running in native iOS app
 * This is a quick sync check - for async detection use detectNativeApp from platform.ts
 */
const isNativeApp = (): boolean => {
  if (typeof window === 'undefined') return false;
  return !!(window.NativelyInfo || window.NativelyHealth || window.natively);
};

/**
 * Check if running on iOS (native or browser)
 */
const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent);
};

/**
 * Get the appropriate redirect URL for OAuth flows
 * For web browsers with polling, uses /oauth-redirect with session ID
 * For native apps, uses /auth for normal deep link flow
 */
const getRedirectUrl = (sessionId?: string): string => {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8080';
  
  // If session ID provided (web browser polling), use oauth-redirect page
  if (sessionId) {
    return `${origin}/oauth-redirect?session_id=${sessionId}`;
  }
  
  // Native app normal flow - redirect to /auth
  return origin.endsWith('/') ? origin.slice(0, -1) : origin;
};

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
    // Detect URL in deep links for native apps
    detectSessionInUrl: true,
    // Use PKCE flow for better mobile support
    // PKCE handles redirects better in mobile browsers and custom URL schemes
    flowType: 'pkce',
    // Store key for iOS WebView - ensures session persists across app restarts
    storageKey: 'gitfit-auth-token',
    // Enable debug mode to track session refresh (remove in production if needed)
    debug: false,
  }
});

/**
 * Get the OAuth redirect URL to use for OAuth flows
 * Call this when initiating OAuth flows to ensure proper redirect
 */
export const getOAuthRedirectUrl = (sessionId?: string): string => {
  return getRedirectUrl(sessionId);
};

/**
 * Get the redirect URL for password reset emails
 * Password reset should ALWAYS go to web version, not native app,
 * because Supabase's PKCE flow doesn't work with universal links
 * (the code_verifier is lost when the link opens in the app)
 */
export const getPasswordResetRedirectUrl = (): string => {
  // Always use the production web URL for password reset
  // This ensures PKCE flow works correctly
  return 'https://gitfit.ai';
};